import { useEffect, useMemo, useRef, useState } from "react";
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { token } from "@atlaskit/tokens";
import Button from '@atlaskit/button/new';
import { subDays } from "date-fns";
import { DateRange, DateRangePicker } from "react-date-range";
import { buildSelection, formatDateInput, formatDateLabel, resolveDatePresetLabel, ReportDateRangeFieldProps, reportDateStaticRanges, dateTriggerStyles, dateTriggerChipStyles } from "./helper";
import './Reports.css';

const clampDate = (date: Date, minDate?: Date, maxDate?: Date) => {
  let nextDate = new Date(date);

  if (minDate && nextDate < minDate) {
    nextDate = new Date(minDate);
  }

  if (maxDate && nextDate > maxDate) {
    nextDate = new Date(maxDate);
  }

  return nextDate;
};

export function ReportDateRangeField({
  endDate,
  isHourly,
  onChange,
  startDate
}: ReportDateRangeFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const [draftRange, setDraftRange] = useState(() => ({
    startDate,
    endDate
  }));
  const selectionRange = useMemo(
    () => buildSelection(draftRange.startDate, draftRange.endDate),
    [draftRange.endDate, draftRange.startDate]
  );
  const presetLabel = useMemo(() => resolveDatePresetLabel(startDate, endDate), [endDate, startDate]);
  const selectedDateLabel = useMemo(
    () => `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`,
    [endDate, startDate]
  );
  const maxSelectableDate = useMemo(() => new Date(), []);
  const minSelectableDate = useMemo(
    () => (isHourly ? subDays(maxSelectableDate, 6) : undefined),
    [isHourly, maxSelectableDate]
  );

  useEffect(() => {
    setDraftRange({
      startDate,
      endDate
    });
  }, [endDate, startDate]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!fieldRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen]);

  const handleDefinedRangeChange = (ranges: { selection?: DateRange }) => {
    const selection = ranges.selection;

    if (!selection?.startDate || !selection?.endDate) {
      return;
    }

    setDraftRange({
      startDate: formatDateInput(selection.startDate),
      endDate: formatDateInput(selection.endDate)
    });
  };

  return (
    <div className="date-control-field-reports" ref={fieldRef}>
      <div className="date-control-trigger-reports">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={() => setIsOpen((current) => !current)}
          style={dateTriggerStyles}
        >
          <span style={dateTriggerChipStyles}>{presetLabel}</span>
          <span style={{ flex: 1, textAlign: 'left' }}>{selectedDateLabel}</span>
          <span aria-hidden="true" style={{ color: token('color.icon.subtle') }}>
            <ChevronDownIcon label=""/>
          </span>
        </button>
      </div>
      {isOpen ? (
        <div className="date-control-picker-reports" role="dialog">
          <DateRangePicker
            className="date-control-picker-reports__calendar"
            direction="horizontal"
            inputRanges={[]}
            maxDate={maxSelectableDate}
            minDate={minSelectableDate}
            months={2}
            moveRangeOnFirstSelection={false}
            onChange={handleDefinedRangeChange}
            ranges={[selectionRange]}
            showSelectionPreview
            staticRanges={reportDateStaticRanges}
          />
          <div className="date-control-footer">
            <Button
              appearance="primary"
              onClick={() => {
                const clampedStartDate = clampDate(
                  new Date(`${draftRange.startDate}T00:00:00`),
                  minSelectableDate,
                  maxSelectableDate
                );
                const clampedEndDate = clampDate(
                  new Date(`${draftRange.endDate}T00:00:00`),
                  minSelectableDate,
                  maxSelectableDate
                );
                const normalizedStartDate = clampedStartDate <= clampedEndDate ? clampedStartDate : clampedEndDate;
                const normalizedEndDate = clampedEndDate >= clampedStartDate ? clampedEndDate : clampedStartDate;

                onChange({
                  startDate: formatDateInput(normalizedStartDate),
                  endDate: formatDateInput(normalizedEndDate)
                });
                setIsOpen(false);
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
